import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import axios from '../../services/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { callMainAPI } from '../../services/authService';
import { useSession } from '../../context/SessionContext';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * SAML Assertion Consumer Service (ACS) Component
 * Handles SAML response processing and user authentication
 */
const SamlAcsPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [processing, setProcessing] = useState(true);
    const [currentStep, setCurrentStep] = useState('Processing SAML response...');
    const [error, setError] = useState('');
    const { refreshAuth } = useAuth();

    useEffect(() => {
        const processSamlResponse = async () => {
            try {
                setCurrentStep('Extracting SAML response...');
                
                // Get SAML response from URL parameters
                const samlResponse = searchParams.get('SAMLResponse');
                const relayState = searchParams.get('RelayState');

                if (!samlResponse) {
                    throw new Error('No SAML response received');
                }

                // Parse relay state to get original parameters, with fallback to sessionStorage
                let originalParams = {};
                if (relayState) {
                    try {
                        originalParams = JSON.parse(relayState);
                    } catch (err) {
                        console.warn('Could not parse relay state:', err);
                    }
                }
                
                // If relay state is empty, try to get parameters from sessionStorage
                const storedParams = sessionStorage.getItem('samlRequestParams');
                
                if (storedParams) {
                    try {
                        const parsedStoredParams = JSON.parse(storedParams);
                        
                        // Merge stored parameters with any existing ones, prioritizing stored params
                        if (!originalParams.userId && parsedStoredParams.userId) {
                            originalParams.userId = parsedStoredParams.userId;
                        }
                        if (!originalParams.memberId && parsedStoredParams.memberId) {
                            originalParams.memberId = parsedStoredParams.memberId;
                        }
                        if (!originalParams.cid && parsedStoredParams.cid) {
                            originalParams.cid = parsedStoredParams.cid;
                        }
                        if (!originalParams.caseId && parsedStoredParams.caseId) {
                            originalParams.caseId = parsedStoredParams.caseId;
                        }
                        if (!originalParams.pPlan && parsedStoredParams.pPlan) {
                            originalParams.pPlan = parsedStoredParams.pPlan;
                        }
                        
                        // Remove the one-time relay params from sessionStorage.
                        // We intentionally do NOT clear localStorage here — those are
                        // the persistent copy that will be available on next re-entry
                        // when the auth cookie is still valid but sessionStorage is gone.
                        sessionStorage.removeItem('samlRequestParams');
                    } catch (err) {
                        console.warn('Could not parse stored SAML params:', err);
                    }
                }

                // Also try to extract parameters from current URL
                const currentUrlParams = new URLSearchParams(window.location.search);
                
                // Only override if we don't already have the parameter
                if (!originalParams.userId) {
                    const extractedUserId = currentUrlParams.get('UserID') || currentUrlParams.get('userId') || currentUrlParams.get('userID') || '';
                    if (extractedUserId) {
                        originalParams.userId = extractedUserId;
                    }
                }
                if (!originalParams.memberId) {
                    const extractedMemberId = currentUrlParams.get('MemberID') || currentUrlParams.get('memberId') || '';
                    if (extractedMemberId) {
                        originalParams.memberId = extractedMemberId;
                    }
                }
                if (!originalParams.cid) {
                    const extractedCid = currentUrlParams.get('CID') || currentUrlParams.get('cid') || '';
                    if (extractedCid) {
                        originalParams.cid = extractedCid;
                    }
                }
                if (!originalParams.caseId) {
                    const extractedCaseId = currentUrlParams.get('CaseID') || currentUrlParams.get('caseId') || '';
                    if (extractedCaseId) {
                        originalParams.caseId = extractedCaseId;
                    }
                }
                if (!originalParams.pPlan) {
                    const extractedPPlan = currentUrlParams.get('SystemID') || currentUrlParams.get('systemId') || currentUrlParams.get('pPlan') || '';
                    if (extractedPPlan) {
                        originalParams.pPlan = extractedPPlan;
                    }
                }

                setCurrentStep('Authenticating with SAML...');

                // Send SAML response to backend for authentication and token generation
                const samlAuthRequest = {
                    samlResponse,
                    userId: originalParams.userId || '',
                    memberId: originalParams.memberId || '',
                    cid: originalParams.cid || '',
                    caseId: originalParams.caseId || '',
                    pPlan: originalParams.pPlan || '',
                    redirectUrl: originalParams.redirectUrl || ''
                };

                const response = await axios.post(`${API_URL}/Samlauth/SAMLAuthenticate`, samlAuthRequest);

                // Handle both possible token property names
                const token = response.data?.Token || response.data?.token;
                
                if (!token) {
                    throw new Error('No token received from SAML authentication');
                }

                // Store authentication data
                sessionStorage.setItem('isAuthenticated', 'true');
                sessionStorage.setItem('authToken', token);
                sessionStorage.setItem('samlResponse', samlResponse);
                
                // ── Storage split ──────────────────────────────────────────────────
                // User identity → localStorage  (survives tab close; drives skip-re-auth)
                const userIdentity = {
                    userId: originalParams.userId || '',
                    isAuthenticated: true
                };
                localStorage.setItem('userIdentity', JSON.stringify(userIdentity));

                // Member context → sessionStorage ONLY  (tab-scoped; fresh per new tab)
                // A new tab that opens with a different MemberID/CID/CaseID in the URL
                // will overwrite this without touching the persisted userId above.
                const memberContext = {
                    memberId: originalParams.memberId || '',
                    cid:      originalParams.cid      || '',
                    caseId:   originalParams.caseId   || '',
                    systemId: originalParams.pPlan    || ''
                };
                sessionStorage.setItem('memberContext', JSON.stringify(memberContext));

                // sessionStorage.user — backward-compat composite kept for any code that
                // still reads it directly (identity + current tab's member context).
                const userInfo = {
                    userId:   originalParams.userId   || '',
                    memberId: originalParams.memberId || '',
                    cid:      originalParams.cid      || '',
                    caseId:   originalParams.caseId   || '',
                    systemId: originalParams.pPlan    || '',
                    isAuthenticated: true
                };
                sessionStorage.setItem('user', JSON.stringify(userInfo));

                // samlRequestParams kept session-only for the relay-state flow;
                // member params must NOT be persisted to localStorage.
                sessionStorage.setItem('samlRequestParams', JSON.stringify({
                    userID:   originalParams.userId   || '',
                    memberId: originalParams.memberId || '',
                    cid:      originalParams.cid      || '',
                    caseId:   originalParams.caseId   || '',
                    systemId: originalParams.pPlan    || ''
                }));

                // Trigger a storage event to notify other components of auth state change
                window.dispatchEvent(new Event('storage'));
                
                // Refresh authentication state
                await refreshAuth();

                // Show success state briefly before redirecting
                setProcessing(false);

                // Small delay to ensure state updates and show success message, then redirect
                setTimeout(async () => {
                    try {
                        // Call Main API using the stored token
                        const apiResponse = await callMainAPI(originalParams);

                        // Ensure API call succeeds before proceeding
                        if (!apiResponse) {
                            throw new Error('Failed to get API response');
                        }

                        // Always redirect to reports list with menuId=1 after successful authentication
                        navigate('/reports-list?menuId=1');
                    } catch (apiError) {
                        console.error('Main API call failed:', apiError);
                        setError('Failed to initialize session. Please try again.');
                        setProcessing(false);
                    }
                }, 1500);
            } catch (err) {
                console.error('SAML processing error:', err);
                setError(
                    err.response?.data?.message || 
                    err.message || 
                    'Failed to process SAML response'
                );
                setProcessing(false);
            }
        };

        processSamlResponse();
    }, [searchParams, navigate]);

    const handleReturnToLogin = () => {
        navigate('/reports-list?menuId=1');
    };

    return (
        <Container fluid className="d-flex align-items-center justify-content-center min-vh-100">
            <Row>
                <Col>
                    <Card className="shadow">
                        <Card.Body className="text-center p-5">
                            {processing ? (
                                <>
                                    <div className="mb-4">
                                        <img 
                                            src="/ajax-loading.gif" 
                                            alt="Loading..." 
                                            style={{ width: '3rem', height: '3rem' }}
                                        />
                                    </div>
                                    <h4 className="mb-3">Processing Authentication</h4>
                                    <p className="text-muted">{currentStep}</p>
                                    <div className="mt-4">
                                        <small className="text-muted">Please wait while we securely authenticate your session...</small>
                                    </div>
                                </>
                            ) : error ? (
                                <>
                                    <Alert variant="danger" className="mb-4">
                                        <Alert.Heading>Authentication Failed</Alert.Heading>
                                        <p>{error}</p>
                                    </Alert>
                                    <button 
                                        className="btn btn-primary"
                                        onClick={handleReturnToLogin}
                                    >
                                        Return to Login
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="text-success mb-3">
                                        <i className="fas fa-check-circle fa-3x"></i>
                                    </div>
                                    <h4>Authentication Successful</h4>
                                    <p className="text-muted">Redirecting to application...</p>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default SamlAcsPage;
